/**
 * useProcessRecipes Hook
 *
 * Provides CRUD operations for recipe_templates and recipe_lines tables.
 * Manages process recipes that define material requirements for production phases.
 *
 * Part of US-P2-010: Process Recipe Management
 */

import { useState, useEffect, useCallback } from 'react'
import { supabase } from '@/supabase-client'

/**
 * Hook to fetch all process recipes with optional filtering
 * @param {Object} options - Filter options
 * @param {string} options.recipeType - Filter by recipe type ('block', 'spawn', 'liquid_culture', 'other')
 * @param {string} options.strainId - Filter by strain ID (null returns strain-agnostic recipes)
 * @param {boolean} options.activeOnly - Only fetch active recipes (default: true)
 * @param {boolean} options.includeLines - Include recipe lines in response (default: true)
 * @returns {Object} { recipes, loading, error, refetch }
 */
export function useProcessRecipes(options = {}) {
  const [recipes, setRecipes] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  const {
    recipeType = null,
    strainId = null,
    activeOnly = true,
    includeLines = true
  } = options

  const fetchRecipes = useCallback(async () => {
    setLoading(true)
    setError(null)

    try {
      let query = supabase
        .from('recipe_templates')
        .select(includeLines ? `
          *,
          strain:strains(id, name, code, origin),
          recipe_lines(
            id,
            inventory_item_id,
            quantity,
            unit,
            per_unit,
            is_optional,
            sequence_order,
            notes,
            inventory_item:inventory_items(id, name, category, unit_of_measure)
          )
        ` : `
          *,
          strain:strains(id, name, code, origin)
        `)
        .order('name')

      if (activeOnly) {
        query = query.eq('is_active', true)
      }

      if (recipeType) {
        query = query.eq('recipe_type', recipeType)
      }

      if (strainId !== undefined && strainId !== null) {
        query = query.eq('strain_id', strainId)
      }

      const { data, error: fetchError } = await query

      if (fetchError) {
        throw fetchError
      }

      // Transform to include lines as sorted array
      const transformedRecipes = (data || []).map(recipe => ({
        ...recipe,
        lines: includeLines
          ? (recipe.recipe_lines || []).sort((a, b) => a.sequence_order - b.sequence_order)
          : []
      }))

      setRecipes(transformedRecipes)
    } catch (err) {
      console.error('[useProcessRecipes] Error:', err)
      setError(err.message)
      setRecipes([])
    } finally {
      setLoading(false)
    }
  }, [recipeType, strainId, activeOnly, includeLines])

  useEffect(() => {
    fetchRecipes()
  }, [fetchRecipes])

  return {
    recipes,
    loading,
    error,
    refetch: fetchRecipes
  }
}

/**
 * Hook to fetch a single recipe by ID with full details
 * @param {string} id - The recipe template UUID
 * @returns {Object} { recipe, lines, loading, error, refetch }
 */
export function useProcessRecipe(id) {
  const [recipe, setRecipe] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  const fetchRecipe = useCallback(async () => {
    if (!id) {
      setRecipe(null)
      setLoading(false)
      return
    }

    setLoading(true)
    setError(null)

    try {
      const { data, error: fetchError } = await supabase
        .from('recipe_templates')
        .select(`
          *,
          strain:strains(id, name, code, category),
          recipe_lines(
            id,
            inventory_item_id,
            quantity,
            unit,
            per_unit,
            is_optional,
            sequence_order,
            notes,
            created_at,
            inventory_item:inventory_items(id, name, category, unit_of_measure)
          )
        `)
        .eq('id', id)
        .single()

      if (fetchError) {
        throw fetchError
      }

      setRecipe({
        ...data,
        lines: (data.recipe_lines || []).sort((a, b) => a.sequence_order - b.sequence_order)
      })
    } catch (err) {
      console.error('[useProcessRecipe] Error:', err)
      setError(err.message)
      setRecipe(null)
    } finally {
      setLoading(false)
    }
  }, [id])

  useEffect(() => {
    fetchRecipe()
  }, [fetchRecipe])

  return {
    recipe,
    lines: recipe?.lines || [],
    loading,
    error,
    refetch: fetchRecipe
  }
}

/**
 * Hook for process recipe mutations (create, update, delete templates and lines)
 * @returns {Object} Mutation functions and state
 */
export function useProcessRecipeMutations() {
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)

  /**
   * Create a new recipe template
   * @param {Object} data - Recipe data
   * @param {string} data.name - Recipe name
   * @param {string} data.description - Optional description
   * @param {string} data.recipe_type - Type: 'block', 'spawn', 'liquid_culture', 'other'
   * @param {string} data.strain_id - Optional strain ID (null = generic)
   * @param {Array} data.lines - Optional initial lines
   * @returns {Promise<Object>} Created recipe
   */
  const createRecipe = useCallback(async (data) => {
    setLoading(true)
    setError(null)

    try {
      // Create recipe template
      const { data: recipe, error: recipeError } = await supabase
        .from('recipe_templates')
        .insert({
          name: data.name,
          description: data.description || null,
          recipe_type: data.recipe_type,
          strain_id: data.strain_id || null,
          is_active: true
        })
        .select()
        .single()

      if (recipeError) {
        throw recipeError
      }

      // Create initial lines if provided
      if (data.lines && data.lines.length > 0) {
        const lines = data.lines.map((line, index) => ({
          recipe_template_id: recipe.id,
          inventory_item_id: line.inventory_item_id,
          quantity: line.quantity,
          unit: line.unit,
          per_unit: line.per_unit,
          is_optional: line.is_optional || false,
          sequence_order: line.sequence_order ?? index + 1,
          notes: line.notes || null
        }))

        const { error: linesError } = await supabase
          .from('recipe_lines')
          .insert(lines)

        if (linesError) {
          // Rollback recipe creation
          await supabase.from('recipe_templates').delete().eq('id', recipe.id)
          throw new Error(`Failed to create recipe lines: ${linesError.message}`)
        }
      }

      return recipe
    } catch (err) {
      console.error('[useProcessRecipeMutations.createRecipe] Error:', err)
      setError(err.message)
      throw err
    } finally {
      setLoading(false)
    }
  }, [])

  /**
   * Update an existing recipe template
   * @param {string} id - Recipe UUID
   * @param {Object} updates - Fields to update
   * @returns {Promise<Object>} Updated recipe
   */
  const updateRecipe = useCallback(async (id, updates) => {
    setLoading(true)
    setError(null)

    try {
      const updateData = {}
      if (updates.name !== undefined) updateData.name = updates.name
      if (updates.description !== undefined) updateData.description = updates.description
      if (updates.recipe_type !== undefined) updateData.recipe_type = updates.recipe_type
      if (updates.strain_id !== undefined) updateData.strain_id = updates.strain_id
      if (updates.is_active !== undefined) updateData.is_active = updates.is_active
      updateData.updated_at = new Date().toISOString()

      const { data: recipe, error: updateError } = await supabase
        .from('recipe_templates')
        .update(updateData)
        .eq('id', id)
        .select()
        .single()

      if (updateError) {
        throw updateError
      }

      return recipe
    } catch (err) {
      console.error('[useProcessRecipeMutations.updateRecipe] Error:', err)
      setError(err.message)
      throw err
    } finally {
      setLoading(false)
    }
  }, [])

  /**
   * Delete a recipe template (soft delete by setting is_active = false)
   * @param {string} id - Recipe UUID
   * @returns {Promise<void>}
   */
  const deleteRecipe = useCallback(async (id) => {
    setLoading(true)
    setError(null)

    try {
      const { error: deleteError } = await supabase
        .from('recipe_templates')
        .update({
          is_active: false,
          updated_at: new Date().toISOString()
        })
        .eq('id', id)

      if (deleteError) {
        throw deleteError
      }
    } catch (err) {
      console.error('[useProcessRecipeMutations.deleteRecipe] Error:', err)
      setError(err.message)
      throw err
    } finally {
      setLoading(false)
    }
  }, [])

  /**
   * Add an ingredient line to a recipe
   * @param {string} recipeId - Recipe template UUID
   * @param {Object} line - Line data
   * @param {string} line.inventory_item_id - Inventory item UUID
   * @param {number} line.quantity - Quantity required
   * @param {string} line.unit - Unit of measure (kg, g, ml, L, each)
   * @param {string} line.per_unit - Scale: 'per_block', 'per_bag', 'per_jar', 'per_batch'
   * @param {boolean} line.is_optional - Whether ingredient is optional
   * @param {number} line.sequence_order - Display order
   * @param {string} line.notes - Optional notes
   * @returns {Promise<Object>} Created line
   */
  const addRecipeLine = useCallback(async (recipeId, line) => {
    setLoading(true)
    setError(null)

    try {
      // Get next sequence order if not provided
      let sequenceOrder = line.sequence_order
      if (sequenceOrder === undefined || sequenceOrder === null) {
        const { data: existingLines } = await supabase
          .from('recipe_lines')
          .select('sequence_order')
          .eq('recipe_template_id', recipeId)
          .order('sequence_order', { ascending: false })
          .limit(1)

        sequenceOrder = (existingLines?.[0]?.sequence_order ?? 0) + 1
      }

      const { data: createdLine, error: lineError } = await supabase
        .from('recipe_lines')
        .insert({
          recipe_template_id: recipeId,
          inventory_item_id: line.inventory_item_id,
          quantity: line.quantity,
          unit: line.unit,
          per_unit: line.per_unit,
          is_optional: line.is_optional || false,
          sequence_order: sequenceOrder,
          notes: line.notes || null
        })
        .select(`
          *,
          inventory_item:inventory_items(id, name, category, unit_of_measure)
        `)
        .single()

      if (lineError) {
        throw lineError
      }

      return createdLine
    } catch (err) {
      console.error('[useProcessRecipeMutations.addRecipeLine] Error:', err)
      setError(err.message)
      throw err
    } finally {
      setLoading(false)
    }
  }, [])

  /**
   * Update an existing recipe line
   * @param {string} lineId - Recipe line UUID
   * @param {Object} updates - Fields to update
   * @returns {Promise<Object>} Updated line
   */
  const updateRecipeLine = useCallback(async (lineId, updates) => {
    setLoading(true)
    setError(null)

    try {
      const updateData = {}
      if (updates.inventory_item_id !== undefined) updateData.inventory_item_id = updates.inventory_item_id
      if (updates.quantity !== undefined) updateData.quantity = updates.quantity
      if (updates.unit !== undefined) updateData.unit = updates.unit
      if (updates.per_unit !== undefined) updateData.per_unit = updates.per_unit
      if (updates.is_optional !== undefined) updateData.is_optional = updates.is_optional
      if (updates.sequence_order !== undefined) updateData.sequence_order = updates.sequence_order
      if (updates.notes !== undefined) updateData.notes = updates.notes

      const { data: updatedLine, error: updateError } = await supabase
        .from('recipe_lines')
        .update(updateData)
        .eq('id', lineId)
        .select(`
          *,
          inventory_item:inventory_items(id, name, category, unit_of_measure)
        `)
        .single()

      if (updateError) {
        throw updateError
      }

      return updatedLine
    } catch (err) {
      console.error('[useProcessRecipeMutations.updateRecipeLine] Error:', err)
      setError(err.message)
      throw err
    } finally {
      setLoading(false)
    }
  }, [])

  /**
   * Remove a recipe line
   * @param {string} lineId - Recipe line UUID
   * @returns {Promise<void>}
   */
  const removeRecipeLine = useCallback(async (lineId) => {
    setLoading(true)
    setError(null)

    try {
      const { error: deleteError } = await supabase
        .from('recipe_lines')
        .delete()
        .eq('id', lineId)

      if (deleteError) {
        throw deleteError
      }
    } catch (err) {
      console.error('[useProcessRecipeMutations.removeRecipeLine] Error:', err)
      setError(err.message)
      throw err
    } finally {
      setLoading(false)
    }
  }, [])

  /**
   * Calculate recipe cost based on current inventory prices
   * Estimates cost using FIFO inventory valuation
   * @param {string} recipeId - Recipe template UUID
   * @param {number} quantity - Number of units (blocks/bags/batches) to calculate for (default: 1)
   * @returns {Promise<Object>} Cost breakdown { total_cost, line_costs[], missing_items[] }
   */
  const calculateRecipeCost = useCallback(async (recipeId, quantity = 1) => {
    setLoading(true)
    setError(null)

    try {
      // Fetch recipe with lines
      const { data: recipe, error: recipeError } = await supabase
        .from('recipe_templates')
        .select(`
          id,
          name,
          recipe_type,
          recipe_lines(
            id,
            inventory_item_id,
            quantity,
            unit,
            per_unit,
            is_optional,
            inventory_item:inventory_items(id, name, category, unit_of_measure)
          )
        `)
        .eq('id', recipeId)
        .single()

      if (recipeError) {
        throw recipeError
      }

      if (!recipe.recipe_lines || recipe.recipe_lines.length === 0) {
        return {
          recipe_id: recipeId,
          recipe_name: recipe.name,
          quantity,
          total_cost: 0,
          line_costs: [],
          missing_items: []
        }
      }

      // Fetch current inventory prices (latest batch cost per item)
      const itemIds = recipe.recipe_lines.map(l => l.inventory_item_id)
      const { data: inventoryBatches, error: batchError } = await supabase
        .from('inventory_batches')
        .select('item_id, unit_cost, quantity_remaining')
        .in('item_id', itemIds)
        .eq('status', 'available')
        .order('received_at', { ascending: true }) // FIFO ordering

      if (batchError) {
        throw batchError
      }

      // Build price map using weighted average of available batches
      const priceMap = {}
      itemIds.forEach(itemId => {
        const batches = inventoryBatches.filter(b => b.item_id === itemId)
        if (batches.length > 0) {
          const totalQty = batches.reduce((sum, b) => sum + Number(b.quantity_remaining || 0), 0)
          const weightedCost = batches.reduce(
            (sum, b) => sum + (Number(b.unit_cost || 0) * Number(b.quantity_remaining || 0)),
            0
          )
          priceMap[itemId] = totalQty > 0 ? weightedCost / totalQty : 0
        }
      })

      // Calculate costs per line
      const lineCosts = []
      const missingItems = []
      let totalCost = 0

      for (const line of recipe.recipe_lines) {
        // Calculate required quantity based on per_unit type
        let requiredQty = Number(line.quantity)
        if (line.per_unit !== 'per_batch') {
          requiredQty *= quantity
        }

        const unitCost = priceMap[line.inventory_item_id]
        const itemName = line.inventory_item?.name || 'Unknown Item'

        if (unitCost === undefined) {
          missingItems.push({
            inventory_item_id: line.inventory_item_id,
            item_name: itemName,
            quantity_required: requiredQty,
            unit: line.unit,
            is_optional: line.is_optional
          })
          lineCosts.push({
            line_id: line.id,
            inventory_item_id: line.inventory_item_id,
            item_name: itemName,
            quantity_required: requiredQty,
            unit: line.unit,
            unit_cost: null,
            line_cost: null,
            is_optional: line.is_optional,
            has_price: false
          })
        } else {
          const lineCost = requiredQty * unitCost
          totalCost += lineCost

          lineCosts.push({
            line_id: line.id,
            inventory_item_id: line.inventory_item_id,
            item_name: itemName,
            quantity_required: requiredQty,
            unit: line.unit,
            unit_cost: unitCost,
            line_cost: lineCost,
            is_optional: line.is_optional,
            has_price: true
          })
        }
      }

      return {
        recipe_id: recipeId,
        recipe_name: recipe.name,
        recipe_type: recipe.recipe_type,
        quantity,
        total_cost: totalCost,
        cost_per_unit: quantity > 0 ? totalCost / quantity : 0,
        line_costs: lineCosts,
        missing_items: missingItems,
        has_complete_pricing: missingItems.length === 0
      }
    } catch (err) {
      console.error('[useProcessRecipeMutations.calculateRecipeCost] Error:', err)
      setError(err.message)
      throw err
    } finally {
      setLoading(false)
    }
  }, [])

  /**
   * Replace all lines in a recipe (useful for bulk updates)
   * @param {string} recipeId - Recipe template UUID
   * @param {Array} lines - New lines to set
   * @returns {Promise<Array>} Created lines
   */
  const replaceRecipeLines = useCallback(async (recipeId, lines) => {
    setLoading(true)
    setError(null)

    try {
      // Delete existing lines
      const { error: deleteError } = await supabase
        .from('recipe_lines')
        .delete()
        .eq('recipe_template_id', recipeId)

      if (deleteError) {
        throw deleteError
      }

      // Insert new lines
      if (lines && lines.length > 0) {
        const newLines = lines.map((line, index) => ({
          recipe_template_id: recipeId,
          inventory_item_id: line.inventory_item_id,
          quantity: line.quantity,
          unit: line.unit,
          per_unit: line.per_unit,
          is_optional: line.is_optional || false,
          sequence_order: line.sequence_order ?? index + 1,
          notes: line.notes || null
        }))

        const { data: createdLines, error: insertError } = await supabase
          .from('recipe_lines')
          .insert(newLines)
          .select(`
            *,
            inventory_item:inventory_items(id, name, category, unit_of_measure)
          `)

        if (insertError) {
          throw insertError
        }

        return createdLines
      }

      return []
    } catch (err) {
      console.error('[useProcessRecipeMutations.replaceRecipeLines] Error:', err)
      setError(err.message)
      throw err
    } finally {
      setLoading(false)
    }
  }, [])

  /**
   * Clear the error state
   */
  const clearError = useCallback(() => setError(null), [])

  return {
    loading,
    error,
    clearError,
    // Recipe template operations
    createRecipe,
    updateRecipe,
    deleteRecipe,
    // Recipe line operations
    addRecipeLine,
    updateRecipeLine,
    removeRecipeLine,
    replaceRecipeLines,
    // Cost calculation
    calculateRecipeCost
  }
}

export default useProcessRecipes
